import { motion } from "framer-motion";
import { useId } from "react";

const letters = [
  {
    id: "s",
    duration: 0.54,
    d: "M272 111C246 47 333 12 272 16C197 20 53 92 61 137C67 174 185 137 226 158C275 184 191 244 102 285C38 315 2 303 20 269C55 207 143 177 230 171",
  },
  {
    id: "i",
    duration: 0.16,
    d: "M229 174C226 190 220 209 226 216C235 226 251 203 260 185",
  },
  {
    id: "v",
    duration: 0.2,
    d: "M257 181C255 204 256 219 266 220C280 220 291 185 299 171C294 194 290 214 299 218C309 221 322 204 330 190",
  },
  {
    id: "a-one",
    duration: 0.2,
    d: "M330 190C338 173 359 170 365 181C369 194 353 216 341 217C331 218 333 201 341 190C352 174 367 171 372 178C369 194 367 210 376 214C384 217 395 203 402 192",
  },
  {
    id: "s-two",
    duration: 0.17,
    d: "M411 176C397 173 390 183 397 192C404 201 418 202 417 211C416 221 398 225 389 217",
  },
  {
    id: "a-two",
    duration: 0.2,
    d: "M424 193C433 174 455 171 461 182C465 195 448 218 436 218C426 218 429 200 438 188C448 175 463 172 468 179C465 197 463 211 472 215C481 218 491 203 499 192",
  },
  {
    id: "n",
    duration: 0.22,
    d: "M498 193C494 205 491 216 492 220C500 204 512 179 526 181C543 183 523 213 534 216C543 219 555 203 562 192",
  },
  {
    id: "k",
    duration: 0.3,
    d: "M544 235C555 193 571 134 586 97C600 63 608 76 592 106C579 130 565 154 553 181C562 175 576 169 583 176C589 183 574 193 562 197C570 198 575 218 588 219C598 219 608 206 614 196",
  },
  {
    id: "e",
    duration: 0.19,
    d: "M615 196C626 201 651 189 649 179C647 169 628 177 623 190C616 207 627 218 643 216C656 215 666 204 674 195",
  },
  {
    id: "r",
    duration: 0.21,
    d: "M674 195C670 207 668 214 670 218C678 195 684 174 693 174C701 174 696 185 692 191C701 181 711 177 719 183",
  },
  {
    id: "n-capital",
    duration: 0.4,
    d: "M720 239C750 205 777 148 798 101C807 81 814 83 811 105C807 137 798 186 793 228C817 184 849 126 884 92",
  },
  {
    id: "p-capital",
    duration: 0.43,
    d: "M882 292C894 238 910 174 931 109C941 79 956 78 948 112C941 139 928 170 917 196C937 158 951 110 979 86C1007 61 1058 72 1062 103C1067 140 1010 181 942 198C920 203 899 203 880 199",
  },
];

const underline = {
  id: "underline",
  duration: 0.42,
  d: "M86 327C273 276 485 251 745 253M805 254C894 252 969 247 1028 252",
};

const draw = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: ({ delay, duration }) => ({
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { delay, duration, ease: "easeInOut" },
      opacity: { delay, duration: 0.01 },
    },
  }),
};

export default function SivaSignature({
  animated = true,
  className,
  color = "currentColor",
  title = "Sivasanker N. P. signature",
  ...props
}) {
  const filterId = `signature-ink-${useId().replace(/:/g, "")}`;
  let elapsed = 0.08;

  return (
    <svg
      className={className}
      viewBox="0 0 1080 350"
      role={title ? "img" : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title || undefined}
      preserveAspectRatio="xMidYMid meet"
      {...props}
    >
      <defs>
        <filter id={filterId} x="-10%" y="-15%" width="120%" height="140%">
          <feDropShadow dx="0" dy="1.5" stdDeviation="1" floodOpacity="0.25" />
        </filter>
      </defs>

      <g
        fill="none"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
        filter={`url(#${filterId})`}
      >
        {letters.map((letter) => {
          const delay = elapsed;
          elapsed += letter.duration * 0.78;

          return (
            <motion.path
              key={letter.id}
              d={letter.d}
              strokeWidth={letter.id === "s" ? 9 : 7}
              variants={draw}
              custom={{ delay, duration: letter.duration }}
              initial={animated ? "hidden" : "visible"}
              animate="visible"
            />
          );
        })}

        <motion.path
          d={underline.d}
          strokeWidth="4"
          variants={draw}
          custom={{ delay: elapsed, duration: underline.duration }}
          initial={animated ? "hidden" : "visible"}
          animate="visible"
        />

        <motion.circle
          cx="275"
          cy="151"
          r="4.5"
          fill={color}
          stroke="none"
          initial={animated ? { opacity: 0, scale: 0 } : { opacity: 1, scale: 1 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: animated ? 0.72 : 0, duration: 0.13 }}
          style={{ transformOrigin: "275px 151px" }}
        />

        <motion.circle
          cx="770"
          cy="255"
          r="4"
          fill={color}
          stroke="none"
          initial={animated ? { opacity: 0, scale: 0 } : { opacity: 1, scale: 1 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: animated ? elapsed + 0.1 : 0, duration: 0.12 }}
          style={{ transformOrigin: "770px 255px" }}
        />

        <motion.circle
          cx="792"
          cy="255"
          r="4"
          fill={color}
          stroke="none"
          initial={animated ? { opacity: 0, scale: 0 } : { opacity: 1, scale: 1 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: animated ? elapsed + 0.16 : 0, duration: 0.12 }}
          style={{ transformOrigin: "792px 255px" }}
        />
      </g>
    </svg>
  );
}
